import { Link, Text } from '@gasbuddy/react-components';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Fragment, memo, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styles from './DealAlertStatus.module.css';

const cx = classnames.bind(styles);

function StatusPrimaryText({
  dealAlertStation,
  didExpire,
  isPwGBMember,
  uploadReceiptExpiryTimestamp,
}) {
  const { t } = useTranslation(['feature', 'miscellaneous']);
  const primaryText = useMemo(() => {
    if (!dealAlertStation) {
      return null;
    }

    const { id: stationId, name: stationName } = dealAlertStation;

    // Note: GetUpside deals will have an receipt expiry timestamp, dynamic discounts will not
    if (!isPwGBMember && uploadReceiptExpiryTimestamp) {
      if (didExpire) {
        return (
          <Fragment>
            {t('uploadBeforeExpiry', { ns: 'feature', timestamp: uploadReceiptExpiryTimestamp })}
          </Fragment>
        );
      }
      return (
        <Fragment>
          <Trans i18nKey="feature:completeTransactionUpload">
            Complete your transaction at <Link to={`/station/${stationId}`}>{{ stationName }}</Link> before time runs out and upload your receipt before {{ timestamp: uploadReceiptExpiryTimestamp }} to get your GasBack!
          </Trans>
        </Fragment>
      );
    }

    if (isPwGBMember) {
      if (didExpire) {
        return uploadReceiptExpiryTimestamp
          ? (
            <Fragment>
              {t('didNotUseCard', { ns: 'feature', timestamp: uploadReceiptExpiryTimestamp })}
            </Fragment>
          ) : (
            <Fragment>
              {t('dealExpired', { ns: 'feature' })}
            </Fragment>
          );
      }

      return (
        <Fragment>
          <Trans i18nKey="feature:autoApplyDiscount">
            Automatically apply the discount to your fill-up when you use your Pay with GasBuddy card at <Link to={`/station/${stationId}`}>{{ stationName }}</Link> before time runs out!
          </Trans>
        </Fragment>
      );
    }

    return null;
  }, [dealAlertStation, didExpire, isPwGBMember, uploadReceiptExpiryTimestamp, t]);

  if (!primaryText) {
    return null;
  }

  return (
    <Text as="p" className={cx('primaryText')} data-testid="primaryText">{primaryText}</Text>
  );
}

StatusPrimaryText.propTypes = {
  dealAlertStation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  }),
  didExpire: PropTypes.bool,
  isPwGBMember: PropTypes.bool,
  uploadReceiptExpiryTimestamp: PropTypes.string,

};

StatusPrimaryText.defaultProps = {
  dealAlertStation: undefined,
  didExpire: false,
  isPwGBMember: false,
  uploadReceiptExpiryTimestamp: undefined,
};

export default memo(StatusPrimaryText);
